"use client";

import { useRef, useState } from "react";
import {
  DataTable,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  INPUT_MONO,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { PlaneDiagram } from "@/components/tools/physics-fields/coulomb-diagrams";
import {
  Failure,
  MEDIA,
  MediumFields,
  type PanelProps,
} from "@/components/tools/physics-fields/coulomb-pair-panels";
import {
  DiagramFrame,
  unitOptions,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { solveSuperposition } from "@/lib/tool-kit/physics-fields/coulomb";
import { superpositionWorking } from "@/lib/tool-kit/physics-fields/coulomb-working";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readPlain, readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  CHARGE_UNITS,
  fromBase,
  SMALL_DISTANCE_UNITS,
} from "@/lib/tool-kit/physics-fields/units";

interface Row {
  id: number;
  q: string;
  x: string;
  y: string;
}

const EXAMPLES: Record<
  string,
  {
    rows: [string, string, string][];
    point: [string, string];
    test: string;
    chargeUnit: string;
    distanceUnit: string;
  }
> = {
  triangle: {
    rows: [
      ["3", "0.3", "0"],
      ["-2", "0", "0.4"],
    ],
    point: ["0", "0"],
    test: "1",
    chargeUnit: "uC",
    distanceUnit: "m",
  },
  bisector: {
    rows: [
      ["1", "-0.5", "0"],
      ["1", "0.5", "0"],
    ],
    point: ["0", "0.5"],
    test: "",
    chargeUnit: "uC",
    distanceUnit: "m",
  },
  dipole: {
    rows: [
      ["1", "-5", "0"],
      ["-1", "5", "0"],
    ],
    point: ["20", "0"],
    test: "",
    chargeUnit: "nC",
    distanceUnit: "cm",
  },
};
const MAX_CHARGES = 6;

export function ManyPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const nextId = useRef(10);
  const make = (rows: [string, string, string][]): Row[] =>
    rows.map(([q, x, y]) => {
      nextId.current += 1;
      return { id: nextId.current, q, x, y };
    });
  const start = EXAMPLES.triangle;
  const [example, setExample] = useState("triangle");
  const [rows, setRows] = useState<Row[]>(() =>
    start.rows.map(([q, x, y], i) => ({ id: i + 1, q, x, y }))
  );
  const [point, setPoint] = useState({ x: start.point[0], y: start.point[1] });
  const [test, setTest] = useState(start.test);
  const [chargeUnit, setChargeUnit] = useState(start.chargeUnit);
  const [distanceUnit, setDistanceUnit] = useState(start.distanceUnit);
  const [medium, setMedium] = useState("vacuum");
  const [eps, setEps] = useState("1");

  const parsed = rows.map((r) => ({
    q: readQuantity(r.q, CHARGE_UNITS, chargeUnit),
    x: readQuantity(r.x, SMALL_DISTANCE_UNITS, distanceUnit),
    y: readQuantity(r.y, SMALL_DISTANCE_UNITS, distanceUnit),
  }));
  const complete = parsed.every(
    (r) => r.q !== null && r.x !== null && r.y !== null
  );
  const px = readQuantity(point.x, SMALL_DISTANCE_UNITS, distanceUnit);
  const py = readQuantity(point.y, SMALL_DISTANCE_UNITS, distanceUnit);
  const testSI = readQuantity(test, CHARGE_UNITS, chargeUnit);
  const permittivity = readPlain(eps);
  const charges = parsed.map((r) => ({
    q: r.q ?? 0,
    x: r.x ?? 0,
    y: r.y ?? 0,
  }));
  const outcome =
    complete && px !== null && py !== null && permittivity !== null
      ? solveSuperposition(charges, { x: px, y: py }, testSI, permittivity)
      : null;
  const value = outcome?.ok ? outcome.value : null;
  const names = strings.manyExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = EXAMPLES[id];
    if (ex) {
      setRows(make(ex.rows));
      setPoint({ x: ex.point[0], y: ex.point[1] });
      setTest(ex.test);
      setChargeUnit(ex.chargeUnit);
      setDistanceUnit(ex.distanceUnit);
      setMedium("vacuum");
      setEps("1");
    }
  };
  const edit = (id: number, patch: Partial<Row>) => {
    setExample("custom");
    setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const place = (value: number) =>
    formatSig(fmt, fromBase(SMALL_DISTANCE_UNITS, distanceUnit, value), sig);

  return (
    <>
      <SelectField
        id="cl-many-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="cl-many-charge-unit"
          label={strings.chargeUnit}
          onChange={setChargeUnit}
          options={unitOptions(CHARGE_UNITS)}
          value={chargeUnit}
        />
        <SelectField
          id="cl-many-distance-unit"
          label={strings.positionUnit}
          onChange={setDistanceUnit}
          options={unitOptions(SMALL_DISTANCE_UNITS)}
          value={distanceUnit}
        />
      </div>
      <div className="space-y-3">
        <p className={LABEL}>{strings.sourcesHeading}</p>
        {rows.map((row, index) => (
          <div
            className="grid grid-cols-[repeat(3,minmax(0,1fr))_auto] items-end gap-2"
            key={row.id}
          >
            {(
              [
                ["q", strings.chargeColumn, row.q],
                ["x", strings.xColumn, row.x],
                ["y", strings.yColumn, row.y],
              ] as const
            ).map(([key, label, text]) => (
              <div className="space-y-1" key={key}>
                <label className={LABEL} htmlFor={`cl-many-${row.id}-${key}`}>
                  {t(strings.sourceField, { n: fmt(index + 1), field: label })}
                </label>
                <input
                  className={INPUT_MONO}
                  id={`cl-many-${row.id}-${key}`}
                  inputMode="decimal"
                  onChange={(event) =>
                    edit(row.id, { [key]: event.target.value })
                  }
                  value={text}
                />
              </div>
            ))}
            <button
              aria-label={t(strings.removeCharge, { n: fmt(index + 1) })}
              className={BUTTON_SMALL}
              disabled={rows.length <= 1}
              onClick={() => setRows(rows.filter((r) => r.id !== row.id))}
              type="button"
            >
              {strings.remove}
            </button>
          </div>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={rows.length >= MAX_CHARGES}
          onClick={() => setRows([...rows, ...make([["", "", ""]])])}
          type="button"
        >
          {strings.addCharge}
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="cl-many-px"
          label={strings.pointX}
          numeric
          onChange={(v) => setPoint({ ...point, x: v })}
          value={point.x}
        />
        <TextField
          id="cl-many-py"
          label={strings.pointY}
          numeric
          onChange={(v) => setPoint({ ...point, y: v })}
          value={point.y}
        />
        <TextField
          hint={strings.testChargeHint}
          id="cl-many-test"
          label={strings.testCharge}
          numeric
          onChange={setTest}
          value={test}
        />
      </div>
      <MediumFields
        eps={eps}
        idPrefix="cl-many"
        medium={medium}
        onEps={(v) => {
          setMedium("custom");
          setEps(v);
        }}
        onMedium={(id) => {
          setMedium(id);
          if (id in MEDIA) {
            setEps(String(MEDIA[id]));
          }
        }}
        strings={strings}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null || px === null || py === null ? (
          <Failure
            error={outcome && !outcome.ok ? outcome.error : "missing"}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.fieldMagnitude}
                value={`${formatSig(fmt, value.magnitude, sig)} N/C`}
              />
              <StatCard
                label={strings.fieldDirection}
                value={`${formatSig(fmt, value.angle, sig)}°`}
              />
              <StatCard
                label={strings.potential}
                value={`${formatSig(fmt, value.potential, sig)} V`}
              />
              <StatCard
                label={strings.forceMagnitude}
                value={
                  value.force === null
                    ? "—"
                    : `${formatSig(fmt, value.force.magnitude, sig)} N`
                }
              />
              <StatCard
                label={strings.forceDirection}
                value={
                  value.force === null
                    ? "—"
                    : `${formatSig(fmt, value.force.angle, sig)}°`
                }
              />
            </StatGrid>
            <DataTable
              caption={strings.contributionCaption}
              head={[
                strings.colCharge,
                strings.colPosition,
                strings.colDistance,
                strings.colEx,
                strings.colEy,
                strings.colMagnitude,
              ]}
              rows={value.contributions.map((c, i) => [
                `q${fmt(i + 1)}`,
                `(${place(c.charge.x)}, ${place(c.charge.y)})`,
                `${formatSig(fmt, c.distance, sig)} m`,
                formatSig(fmt, c.ex, sig),
                formatSig(fmt, c.ey, sig),
                formatSig(fmt, c.magnitude, sig),
              ])}
            />
            <WorkingLines
              heading={strings.working}
              lines={superpositionWorking(value, testSI, fmt, sig)}
            />
            <DiagramFrame caption={strings.planeCaption}>
              <PlaneDiagram
                charges={charges}
                contributions={value.contributions.map((c) => ({
                  x: c.ex,
                  y: c.ey,
                }))}
                description={strings.planeDescription}
                id="cl-plane-diagram"
                label={strings.planeLabel}
                point={{ x: px, y: py }}
                total={{ x: value.ex, y: value.ey }}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}
